import { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import grey from '@mui/material/colors/grey';
import {
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';

import TableSettings from './TableSettings';

declare module '@mui/x-data-grid-pro' {
  interface ToolbarPropsOverrides {
    title?: string;
    toolbarActions?: ReactNode;
    toolbarFilters?: ReactNode;
    setFiltersAnchor: Dispatch<SetStateAction<HTMLElement | null>>;
    setColumnsAnchor: Dispatch<SetStateAction<HTMLElement | null>>;
  }
}

type ToolbarProps = {
  title?: string;
  toolbarActions?: ReactNode;
  toolbarFilters?: ReactNode;
  setFiltersAnchor: Dispatch<SetStateAction<HTMLElement | null>>;
  setColumnsAnchor: Dispatch<SetStateAction<HTMLElement | null>>;
};

const Toolbar: FC<ToolbarProps> = ({
  title,
  toolbarActions,
  toolbarFilters,
  setFiltersAnchor,
  setColumnsAnchor,
}) => {
  const { t } = useTranslation('dataGrid');

  return (
    <Stack sx={{ gap: 2, p: 2 }}>
      {title && <Typography variant='h6'>{title}</Typography>}

      <Stack direction='row' gap={2} alignItems='center'>
        <GridToolbarQuickFilter
          variant='outlined'
          label={t('toolbarQuickFilterLabel')}
          placeholder={t('toolbarQuickFilterPlaceholder')}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{ color: 'text.secondary', width: 20, height: 20 }}
              />
            ),
          }}
        />
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <GridToolbarFilterButton
            ref={setFiltersAnchor}
            componentsProps={{
              button: {
                sx: {
                  width: 'max-content',
                  bgcolor: grey['100'],
                  color: 'text.secondary',
                  boxShadow: 'none',
                  textTransform: 'none',
                  px: 3,
                  py: 2,

                  ['&:hover']: {
                    bgcolor: grey['200'],
                  },
                },
                startIcon: <FilterAltIcon sx={{ width: 20, height: 20 }} />,
              },
            }}
          />
          {toolbarFilters}
        </Stack>

        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            ml: 'auto',
          }}
        >
          {toolbarActions}
          <TableSettings setAnchor={setColumnsAnchor} />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Toolbar;
